// Army improvements: after each league game (or bye, or voided game) one unit is drawn and its owner
// picks one of two options: a positive trait it doesn't have and can use, or +1 Move (which can come up
// again). Draws come from the league seed, the player and the improvement's number, so every device
// shows the same ones. Saved choices are permanent; the army a game uses has them all.
import { TRAITS, TRAIT_CLASHES } from '../games/capture-the-flag/units';
import { decodeTeam, encodeTeam, MOVE_RANGE } from '../games/capture-the-flag/teamcode';
import type { TraitId, UnitType } from '../games/capture-the-flag/types';
import type { Army } from './api';

export interface Improvement { unit: number; choice: string; at?: number; round?: number }
export interface Draw { unit: number; options: string[] }

const POSITIVE = (Object.keys(TRAITS) as TraitId[]).filter(t => TRAITS[t].up);

export function choiceName(choice: string): string {
  return choice === 'move' ? '+1 Move' : TRAITS[choice as TraitId]?.name ?? choice;
}
export function choiceText(choice: string, unit?: UnitType): string {
  if (choice === 'move') return unit ? `Move ${unit.move} → ${unit.move + 1}. Can come up again.` : 'One more hex of movement.';
  return TRAITS[choice as TraitId]?.text ?? '';
}

// What a unit could still gain.
export function eligible(u: UnitType): string[] {
  const has = new Set(u.traits);
  const out: string[] = POSITIVE.filter(t =>
    !has.has(t)
    && !TRAIT_CLASHES.some(([a, b]) => (a === t && has.has(b)) || (b === t && has.has(a)))
    && !(t === 'sentinel' && u.zone.length === 0));
  if (u.move < MOVE_RANGE[1]) out.push('move');
  return out;
}

export function applyTo(squad: UnitType[], imps: Improvement[]): UnitType[] {
  const s = squad.map(u => ({ ...u, traits: [...u.traits], zone: u.zone.map(z => [z[0], z[1]] as [number, number]) }));
  for (const im of imps) {
    const u = s[im.unit];
    if (!u) continue;
    if (im.choice === 'move') u.move = Math.min(MOVE_RANGE[1], u.move + 1);
    else if (!u.traits.includes(im.choice as TraitId) && im.choice in TRAITS) u.traits.push(im.choice as TraitId);
  }
  return s;
}

// The army's units now (with its improvements), or null if its code is unreadable.
export function currentSquad(army: Army & { improvements?: Improvement[] }): UnitType[] | null {
  if (!army.code) return null;
  try { return applyTo(decodeTeam(army.code).squad, army.improvements ?? []); } catch { return null; }
}

// The team code a game uses: the army with every improvement so far.
export function improvedCode(army: Army & { improvements?: Improvement[] }): string | undefined {
  if (!army.code || !army.improvements?.length) return army.code;
  try { const t = decodeTeam(army.code); return encodeTeam({ name: t.name, squad: applyTo(t.squad, army.improvements) }); } catch { return army.code; }
}

function hash(...parts: (string | number)[]): number {
  let h = 2166136261;
  const s = parts.join('|');
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Improvement number n for a player: the unit, and up to two options. A unit with nothing left to gain
// is skipped for the next one in the squad.
export function draw(seed: number, playerId: string, n: number, squad: UnitType[]): Draw | null {
  const r = rng(hash(seed, playerId, 'improve', n));
  const start = Math.floor(r() * squad.length);
  for (let k = 0; k < squad.length; k++) {
    const unit = (start + k) % squad.length;
    const opts = eligible(squad[unit]);
    if (!opts.length) continue;
    for (let i = opts.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [opts[i], opts[j]] = [opts[j], opts[i]]; }
    return { unit, options: opts.slice(0, 2) };
  }
  return null;
}
